'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { EmbassyStatusCheckResponse } from '@/app/api/anthropic/embassy-status-check/route';
import type { ApiResponse } from '@/lib/types/anthropic';

interface Booking {
  id: string;
  pnr: string;
  destination: string;
  departureDate: string;
  returnDate: string;
  travelers: number;
  airline: string;
  flightNumber: string;
}

interface EmbassyStatusCheckerProps {
  bookings: Booking[];
}

export default function EmbassyStatusChecker({ bookings }: EmbassyStatusCheckerProps) {
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);
  const [embassyStatus, setEmbassyStatus] = useState<EmbassyStatusCheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nationality, setNationality] = useState('United Kingdom');

  const handleCheckStatus = async (booking: Booking) => {
    setSelectedBooking(booking.id);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/anthropic/embassy-status-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking: {
            destination: booking.destination,
            departureDate: booking.departureDate,
            returnDate: booking.returnDate,
            nationality: nationality,
            travelers: booking.travelers,
            airline: booking.airline,
            flightNumber: booking.flightNumber,
            pnr: booking.pnr,
          },
        }),
      });

      const data: ApiResponse<EmbassyStatusCheckResponse> = await res.json();

      if (data.success) {
        setEmbassyStatus(data.data);
      } else {
        setError(data.error.message);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check embassy status.';
      setError(errorMessage);
      console.error('Error checking embassy status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'expiring-soon':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'expired':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return 'CheckCircleIcon';
      case 'expiring-soon':
        return 'ExclamationTriangleIcon';
      case 'expired':
        return 'XCircleIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-brand p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Icon name="BuildingOffice2Icon" size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              Embassy Status Checker
            </h3>
            <p className="text-sm text-muted-foreground">
              Live verification requirements by destination
            </p>
          </div>
        </div>
      </div>

      {/* Nationality Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Your Nationality
        </label>
        <select
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
          className="w-full px-4 py-2 border border-border rounded-lg bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="United Kingdom">United Kingdom</option>
          <option value="United States">United States</option>
          <option value="India">India</option>
          <option value="China">China</option>
          <option value="Germany">Germany</option>
          <option value="France">France</option>
          <option value="Australia">Australia</option>
          <option value="Canada">Canada</option>
        </select>
      </div>

      {/* Booking Selection */}
      <div className="space-y-3 mb-6">
        <h4 className="text-sm font-semibold text-foreground">Select Booking to Check</h4>
        {bookings.map((booking) => (
          <button
            key={booking.id}
            onClick={() => handleCheckStatus(booking)}
            disabled={loading}
            className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
              selectedBooking === booking.id
                ? 'border-primary bg-primary/5' :'border-border bg-surface hover:bg-muted'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-foreground">{booking.destination}</p>
                <p className="text-sm text-muted-foreground">
                  {booking.departureDate} • PNR: {booking.pnr}
                </p>
              </div>
              <Icon name="ChevronRightIcon" size={20} className="text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Embassy Status Results */}
      {embassyStatus && !loading && (
        <div className="space-y-6">
          {/* Destination Info */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="MapPinIcon" size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  {embassyStatus.destination.country}
                </h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-semibold">Embassy:</span> {embassyStatus.destination.embassyLocation}</p>
                  <p><span className="font-semibold">Contact:</span> {embassyStatus.destination.contactInfo}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Live Status */}
          <div className={`rounded-xl p-6 border-2 ${
            embassyStatus.liveStatus.embassyOperational
              ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                embassyStatus.liveStatus.embassyOperational ? 'bg-green-600' : 'bg-red-600'
              }`}>
                <Icon name="SignalIcon" size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Live Embassy Status</h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="font-semibold">Status:</span>{' '}
                    <span className={embassyStatus.liveStatus.embassyOperational ? 'text-green-700' : 'text-red-700'}>
                      {embassyStatus.liveStatus.embassyOperational ? 'Operational' : 'Closed/Limited'}
                    </span>
                  </p>
                  <p><span className="font-semibold">Processing Time:</span> {embassyStatus.liveStatus.currentProcessingTime}</p>
                  <p><span className="font-semibold">Appointments:</span> {embassyStatus.liveStatus.appointmentAvailability}</p>
                </div>
                {embassyStatus.liveStatus.alerts.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {embassyStatus.liveStatus.alerts.map((alert, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Icon name="BellAlertIcon" size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-700">{alert}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Visa Types */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={20} className="text-blue-600" />
              Visa Types & Requirements
            </h4>
            <div className="space-y-3">
              {embassyStatus.visaTypes.map((visa, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-bold text-gray-900">{visa.type}</h5>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      visa.required ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {visa.required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-2">
                    <div>
                      <span className="text-gray-600">Processing:</span>
                      <span className="ml-2 font-semibold text-gray-900">{visa.processingTime}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Validity:</span>
                      <span className="ml-2 font-semibold text-gray-900">{visa.validity}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Cost:</span>
                      <span className="ml-2 font-semibold text-gray-900">{visa.cost}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 italic">{visa.notes}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Document Validity */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Icon name="IdentificationIcon" size={20} className="text-purple-600" />
              Document Validity Status
            </h4>
            <div className="space-y-3">
              {embassyStatus.documentValidity.map((doc, idx) => (
                <div key={idx} className={`border rounded-lg p-4 ${getStatusColor(doc.status)}`}>
                  <div className="flex items-start gap-3">
                    <Icon name={getStatusIcon(doc.status) as any} size={20} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-bold">{doc.documentType}</h5>
                        <span className="text-xs font-semibold uppercase">{doc.status.replace('-', ' ')}</span>
                      </div>
                      <p className="text-sm mb-1">
                        <span className="font-semibold">Minimum Validity:</span> {doc.minimumValidity}
                      </p>
                      <p className="text-xs italic">{doc.notes}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Verification Requirements */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-green-600" />
              Verification Requirements
            </h4>
            <div className="space-y-4">
              {embassyStatus.verificationRequirements.map((category, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <h5 className="font-bold text-gray-900 mb-3">{category.category}</h5>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-2">
                        <Icon 
                          name={item.required ? "ExclamationCircleIcon" : "InformationCircleIcon"} 
                          size={16} 
                          className={`flex-shrink-0 mt-0.5 ${
                            item.required ? 'text-red-600' : 'text-blue-600'
                          }`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {item.name} {item.required && <span className="text-red-600">*</span>}
                          </p>
                          <p className="text-xs text-gray-600">{item.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className="font-semibold">Verification:</span> {item.verificationMethod}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {embassyStatus.recommendations.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="LightBulbIcon" size={20} className="text-yellow-600" />
                Recommendations
              </h4>
              <ul className="space-y-2">
                {embassyStatus.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">{rec}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}